import React, { useState, useEffect } from 'react';

interface PlayGameProps {
  initialGameCode: string;
  onBackToMenu: () => void;
}

interface GameData {
  theme: string;
  words: string[];
}

interface GridPosition {
  row: number;
  col: number;
}

interface PlacedWord {
  word: string;
  positions: GridPosition[];
}

export const PlayGame: React.FC<PlayGameProps> = ({ initialGameCode, onBackToMenu }) => {
  const [gameCode, setGameCode] = useState<string>(initialGameCode);
  const [gameData, setGameData] = useState<GameData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [gameBoard, setGameBoard] = useState<string[]>(Array(48).fill(''));
  const [selectedPositions, setSelectedPositions] = useState<number[]>([]);
  const [foundWords, setFoundWords] = useState<PlacedWord[]>([]);
  const [isGameCompleted, setIsGameCompleted] = useState<boolean>(false);
  const [placedWords, setPlacedWords] = useState<PlacedWord[]>([]);
  const [copySuccess, setCopySuccess] = useState<string>("");

  // Grid dimensions (6 columns x 8 rows = 48 cells)
  const GRID_ROWS = 8;
  const GRID_COLS = 6;

  useEffect(() => {
    if (gameCode) {
      loadGame(gameCode);
    } else {
      setLoading(false);
    }
  }, [gameCode]);

  const loadGame = (code: string) => {
    setLoading(true);
    setError(null);

    try {
      // Decode game data from base64
      const decodedData = JSON.parse(atob(code));

      if (!decodedData.theme || !decodedData.words || !Array.isArray(decodedData.words)) {
        throw new Error('Invalid game data');
      }

      setGameData(decodedData);

      // Create game board by placing words on the grid
      const board = createGameBoard(decodedData.words);
      setGameBoard(board);

      setLoading(false);
    } catch (err) {
      setError('Failed to load game. Invalid game code.');
      setLoading(false);
    }
  };

  const createGameBoard = (words: string[]): string[] => {
    // Create an 8x6 grid
    const grid: string[] = Array(GRID_ROWS * GRID_COLS).fill('');
    const usedWords: PlacedWord[] = [];

    // Sort words by length in descending order to place longest words first
    const sortedWords = [...words].sort((a, b) => b.length - a.length);

    const directions = [
      { rowDir: 0, colDir: 1 },  // Horizontal
      { rowDir: 1, colDir: 0 },  // Vertical
      { rowDir: 1, colDir: 1 },  // Diagonal down-right
      { rowDir: 1, colDir: -1 }, // Diagonal down-left
      { rowDir: -1, colDir: 1 }, // Diagonal up-right
      { rowDir: -1, colDir: -1 } // Diagonal up-left
    ];

    const tryPlaceWord = (word: string) => {
      // Try 30 random placements before giving up
      for (let attempt = 0; attempt < 30; attempt++) {
        // Pick a random direction
        const direction = directions[Math.floor(Math.random() * directions.length)];

        // Choose a random starting point
        let startRow, startCol;
        if (direction.rowDir >= 0) {
          startRow = Math.floor(Math.random() * (GRID_ROWS - word.length * Math.abs(direction.rowDir) + 1));
        } else {
          startRow = Math.floor(Math.random() * (GRID_ROWS - 1) + word.length * Math.abs(direction.rowDir));
        }

        if (direction.colDir >= 0) {
          startCol = Math.floor(Math.random() * (GRID_COLS - word.length * Math.abs(direction.colDir) + 1));
        } else {
          startCol = Math.floor(Math.random() * (GRID_COLS - 1) + word.length * Math.abs(direction.colDir));
        }

        // Check if word fits in chosen position with overlap
        let canPlace = true;
        let hasOverlap = false;
        const positions: GridPosition[] = [];

        for (let i = 0; i < word.length; i++) {
          const row = startRow + i * direction.rowDir;
          const col = startCol + i * direction.colDir;
          const index = row * GRID_COLS + col;

          // Make sure the position is valid
          if (row < 0 || row >= GRID_ROWS || col < 0 || col >= GRID_COLS) {
            canPlace = false;
            break;
          }

          // Check if the cell is already filled and if it's compatible
          if (grid[index] && grid[index] !== word[i].toUpperCase()) {
            canPlace = false;
            break;
          }

          // If the cell already has the same letter, it's an overlap
          if (grid[index] === word[i].toUpperCase()) {
            hasOverlap = true;
          }

          positions.push({ row, col });
        }

        // We want at least one letter to overlap with existing words (except for first word)
        if (usedWords.length > 0 && !hasOverlap) {
          canPlace = false;
        }

        if (canPlace) {
          // Place the word
          for (let i = 0; i < positions.length; i++) {
            const pos = positions[i];
            const index = pos.row * GRID_COLS + pos.col;
            grid[index] = word[i].toUpperCase();
          }

          usedWords.push({
            word,
            positions
          });

          return true;
        }
      }

      return false;
    };

    // Try to place each word
    let allWordsPlaced = true;
    for (const word of sortedWords) {
      const placed = tryPlaceWord(word);

      if (!placed) {
        // If we couldn't place the word with overlap, try without overlap requirement
        allWordsPlaced = false;

        // Try again without overlap requirement
        const positions: GridPosition[] = [];
        let bestPlacement = null;
        let maxOverlaps = -1;

        // Try each possible placement and find the one with most overlap with existing letters
        for (const direction of directions) {
          for (let startRow = 0; startRow < GRID_ROWS; startRow++) {
            for (let startCol = 0; startCol < GRID_COLS; startCol++) {
              const testPositions: GridPosition[] = [];
              let canPlace = true;
              let overlaps = 0;

              for (let i = 0; i < word.length; i++) {
                const row = startRow + i * direction.rowDir;
                const col = startCol + i * direction.colDir;

                if (row < 0 || row >= GRID_ROWS || col < 0 || col >= GRID_COLS) {
                  canPlace = false;
                  break;
                }

                const index = row * GRID_COLS + col;
                if (grid[index] && grid[index] !== word[i].toUpperCase()) {
                  canPlace = false;
                  break;
                }

                if (grid[index] === word[i].toUpperCase()) {
                  overlaps++;
                }

                testPositions.push({ row, col });
              }

              if (canPlace && overlaps > maxOverlaps) {
                maxOverlaps = overlaps;
                bestPlacement = [...testPositions];
              }
            }
          }
        }

        // Place the word at the best position or horizontally if no good placement found
        if (bestPlacement) {
          positions.push(...bestPlacement);
        } else {
          const startRow = Math.floor(Math.random() * GRID_ROWS);
          const startCol = Math.floor(Math.random() * (GRID_COLS - word.length + 1));

          for (let i = 0; i < word.length; i++) {
            positions.push({
              row: startRow,
              col: startCol + i
            });
          }
        }

        // Update grid with this word
        for (let i = 0; i < positions.length; i++) {
          const pos = positions[i];
          const index = pos.row * GRID_COLS + pos.col;
          grid[index] = word[i].toUpperCase();
        }

        usedWords.push({
          word,
          positions
        });
      }
    }

    // Fill empty cells with random letters
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const filledGrid = grid.map(cell => cell || letters.charAt(Math.floor(Math.random() * letters.length)));

    setPlacedWords(usedWords);
    return filledGrid;
  };

  const getNeighbors = (index: number): number[] => {
    const row = Math.floor(index / GRID_COLS);
    const col = index % GRID_COLS;
    const neighbors: number[] = [];

    // Check all 8 directions (including diagonals)
    for (let r = -1; r <= 1; r++) {
      for (let c = -1; c <= 1; c++) {
        if (r === 0 && c === 0) continue; // Skip the center

        const newRow = row + r;
        const newCol = col + c;

        // Ensure the neighbor is within grid bounds
        if (newRow >= 0 && newRow < GRID_ROWS && newCol >= 0 && newCol < GRID_COLS) {
          neighbors.push(newRow * GRID_COLS + newCol);
        }
      }
    }

    return neighbors;
  };

  const isPositionPartOfFoundWord = (row: number, col: number): boolean => {
    return foundWords.some(word =>
      word.positions.some(pos => pos.row === row && pos.col === col)
    );
  };

  const handleTileClick = (index: number) => {
    const row = Math.floor(index / GRID_COLS);
    const col = index % GRID_COLS;

    // Check if this tile is part of an already found word
    if (isPositionPartOfFoundWord(row, col)) {
      return;
    }

    // If no selection yet, start a new selection
    if (selectedPositions.length === 0) {
      setSelectedPositions([index]);
      return;
    }

    // If already selected, deselect if it's the last one selected
    if (selectedPositions.includes(index)) {
      if (selectedPositions[selectedPositions.length - 1] === index) {
        setSelectedPositions(selectedPositions.slice(0, -1));
      }
      return;
    }

    // Check if the new tile is a neighbor of the last selected tile
    const lastSelected = selectedPositions[selectedPositions.length - 1];
    const neighbors = getNeighbors(lastSelected);

    if (neighbors.includes(index)) {
      setSelectedPositions([...selectedPositions, index]);
    }
  };

  const handleSubmitSelection = () => {
    if (selectedPositions.length < 4) {
      alert('Select at least 4 letters to form a word.');
      return;
    }

    const selectedWord = selectedPositions.map(index => gameBoard[index]).join('');

    // Check if the selected word is valid
    const isValid = gameData?.words.some(word =>
      word.toUpperCase() === selectedWord.toUpperCase()
    );

    if (isValid) {
      // Get the word that was found
      const wordFound = gameData?.words.find(word =>
        word.toUpperCase() === selectedWord.toUpperCase()
      );

      // Create position objects for the found word
      const positions = selectedPositions.map(index => ({
        row: Math.floor(index / GRID_COLS),
        col: index % GRID_COLS
      }));

      // Add to found words
      const newFoundWord: PlacedWord = {
        word: wordFound || selectedWord,
        positions
      };

      setFoundWords([...foundWords, newFoundWord]);
      setSelectedPositions([]);

      // Check if all words are found
      if (foundWords.length + 1 >= (gameData?.words.length || 0)) {
        setIsGameCompleted(true);
      }
    } else {
      alert('Not a valid word in this puzzle!');
    }
  };

  const handleClearSelection = () => {
    setSelectedPositions([]);
  };

  const handleGameCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const input = document.getElementById('gameCodeInput') as HTMLInputElement;
    if (input.value) {
      setGameCode(input.value);
    }
  };

  const handleShare = () => {
    // Generate a shareable link using the current game code
    const shareableLink = `${window.location.origin}?code=${gameCode}`;

    // Copy to clipboard
    navigator.clipboard.writeText(shareableLink)
      .then(() => {
        setCopySuccess("Link copied!");
        // Reset status after 2 seconds
        setTimeout(() => setCopySuccess(""), 2000);
      })
      .catch(err => {
        console.error('Failed to copy link: ', err);
        setCopySuccess("Failed to copy");
      });
  };

  return (
    <div className="w-full h-full flex flex-col p-2 pl-0 md:p-6">
      <div className="flex items-center mb-6">
        <button
          onClick={onBackToMenu}
          className="btn !pl-3 text-white font-bold relative bottom-0.5 !transform-none !shadow-none"
          aria-label="Back to menu"
        >
          {"<"}
        </button>
        <h1 className="font-raleway text-2xl font-extrabold text-white uppercase">
          Strands<span className="text-selected">.</span>
        </h1>
        <button
          onClick={handleShare}
          className={`btn ml-auto bg-white text-black font-bold ${copySuccess ? "bg-green-400" : ""}`}
        >
          {copySuccess ? <p className="text-white">Copied <span className="text-white">✓</span></p> : "Share"}
        </button>
      </div>

      {loading ? (
        <div className="flex-1 flex flex-col items-center justify-center">
          <h2 className="text-xl font-bold mb-4 text-center text-white">
            Loading game...
          </h2>
        </div>
      ) : error || !gameData ? (
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="text-center p-8 rounded-lg max-w-md bg-white">
            <h2 className="font-raleway text-2xl font-bold mb-4">{error || 'Enter a game code to play'}</h2>

            <form onSubmit={handleGameCodeSubmit} className="flex flex-col items-center mb-4">
              <input
                id="gameCodeInput"
                type="text"
                placeholder="Enter game code"
                className="px-4 py-2 rounded mb-4 w-64 border border-gray-300"
              />
              <button type="submit" className="btn bg-selected text-white font-bold">
                Play Game
              </button>
            </form>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col">
          <div className="mb-4 bg-white p-4 rounded-lg">
            <div className="flex justify-between items-center">
              <div className="font-bold">
                Theme: {isGameCompleted ? gameData.theme : '???'}
              </div>
              <div>
                Words found: {foundWords.length}/{gameData.words.length}
              </div>
            </div>
          </div>

          {isGameCompleted ? (
            <div className="bg-white p-6 rounded-lg text-center">
              <div className="text-2xl font-bold mb-4">🎉 Puzzle Completed! 🎉</div>
              <div className="text-xl mb-2">Theme: {gameData.theme}</div>
              <div className="mt-4">
                Words: {gameData.words.join(', ')}
              </div>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <div className="flex flex-wrap gap-2 mb-4">
                  {foundWords.map((placedWord, index) => (
                    <div key={index} className="bg-[var(--color-revealed)] px-3 py-1 rounded text-white">
                      {placedWord.word}
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-6 gap-1 mb-4 max-w-sm sm:max-w-md md:max-w-lg mx-auto">
                {gameBoard.map((letter, index) => {
                  const row = Math.floor(index / GRID_COLS);
                  const col = index % GRID_COLS;
                  const isSelected = selectedPositions.includes(index);
                  const isPartOfFoundWord = isPositionPartOfFoundWord(row, col);

                  return (
                    <div
                      key={index}
                      className={`
                        w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 flex items-center justify-center
                        font-bold text-base md:text-lg rounded-md relative
                        ${isSelected ? 'bg-selected text-white cursor-pointer' :
                          isPartOfFoundWord ? 'bg-[var(--color-revealed)] text-white cursor-default' :
                          'bg-white text-black cursor-pointer'}
                        transition-colors duration-200 hover:opacity-90
                      `}
                      onClick={() => handleTileClick(index)}
                    >
                      {letter}
                      {isSelected && (
                        <span className="absolute top-0 right-0 text-xs bg-white text-blue-600 rounded-full w-4 h-4 flex items-center justify-center">
                          {selectedPositions.indexOf(index) + 1}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {selectedPositions.length > 0 && (
                <div className="flex justify-center gap-4 mt-4">
                  <div className="bg-white px-4 py-2 rounded font-bold">
                    {selectedPositions.map(i => gameBoard[i]).join('')}
                  </div>
                  <button
                    onClick={handleSubmitSelection}
                    className="btn bg-selected text-white"
                  >
                    Submit
                  </button>
                  <button
                    onClick={handleClearSelection}
                    className="btn bg-white text-black"
                  >
                    Clear
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayGame;
